import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import {
  Mail, CheckCircle2, XCircle, AlertTriangle, Loader2, Plus,
  RefreshCcw, Copy, Activity, History, Download, Siren, Slack, ShieldAlert, ClipboardList,
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import DomainsCard from '@/components/email-config/DomainsCard';
import DmarcCheckCard from '@/components/email-config/DmarcCheckCard';
import DmarcReportsCard from '@/components/email-config/DmarcReportsCard';
import DmarcBlocklistCard from '@/components/email-config/DmarcBlocklistCard';
import DigestSubjectMetricsCard from '@/components/email-config/DigestSubjectMetricsCard';
import PagerDutyCard from '@/components/email-config/PagerDutyCard';
import ReadinessHistoryCard from '@/components/email-config/ReadinessHistoryCard';
import WatchdogCard from '@/components/email-config/WatchdogCard';
import DigestCard from '@/components/email-config/DigestCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EmailConfigPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [config, setConfig] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [editedSender, setEditedSender] = useState('');
  const [savingSender, setSavingSender] = useState(false);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [domainRecords, setDomainRecords] = useState({});
  const [checking, setChecking] = useState(false);
  const [checkResults, setCheckResults] = useState(null);
  const [dmarcDomain, setDmarcDomain] = useState('');
  const [dmarcChecking, setDmarcChecking] = useState(false);
  const [dmarcResult, setDmarcResult] = useState(null);
  const [dmarcSummary, setDmarcSummary] = useState(null);
  const [blocklistStats, setBlocklistStats] = useState(null);
  const [blocklistLoading, setBlocklistLoading] = useState(false);
  const [dmarcUploading, setDmarcUploading] = useState(false);
  const [dmarcDays, setDmarcDays] = useState(30);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [watchdog, setWatchdog] = useState(null);
  const [savingWatchdog, setSavingWatchdog] = useState(false);
  const [runningWatchdog, setRunningWatchdog] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [webhookCfg, setWebhookCfg] = useState(null);
  const [digest, setDigest] = useState(null);
  const [savingDigest, setSavingDigest] = useState(false);
  const [runningDigest, setRunningDigest] = useState(false);

  useEffect(() => { load(); loadHistory(); loadWatchdog(); loadWebhookCfg(); loadDigest(); }, []);

  const loadWebhookCfg = async () => {
    try {
      const res = await axios.get(`${API}/emails/webhook-config`, { headers });
      setWebhookCfg(res.data);
    } catch (e) { /* non-fatal */ }
  };

  const copyWebhookUrl = () => {
    if (!webhookCfg?.webhook_url) return;
    navigator.clipboard.writeText(webhookCfg.webhook_url);
    toast.success('Webhook URL copied to clipboard');
  };

  const loadWatchdog = async () => {
    try {
      const res = await axios.get(`${API}/email-config/watchdog/schedule`, { headers });
      setWatchdog(res.data);
    } catch (e) {
      // non-fatal
    }
  };

  const saveWatchdog = async () => {
    if (!watchdog) return;
    setSavingWatchdog(true);
    try {
      const payload = {
        enabled: !!watchdog.enabled,
        day_of_week: watchdog.day_of_week || 'mon',
        hour: Number(watchdog.hour ?? 7),
        recipient_email: (watchdog.recipient_email || '').trim(),
        slack_webhook_url: (watchdog.slack_webhook_url || '').trim(),
        alert_threshold_pct: Number(watchdog.alert_threshold_pct ?? 100),
      };
      const res = await axios.put(`${API}/email-config/watchdog/schedule`, payload, { headers });
      setWatchdog(res.data);
      toast.success(payload.enabled ? 'Watchdog armed — will alert on regression' : 'Watchdog disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingWatchdog(false); }
  };

  const runWatchdogNow = async () => {
    setRunningWatchdog(true);
    try {
      const res = await axios.post(`${API}/email-config/watchdog/run-now`, {}, { headers });
      const { passed, total, pass_rate_pct, alerted, alerted_email, alerted_slack, threshold } = res.data;
      const channels = [alerted_email && 'email', alerted_slack && 'Slack'].filter(Boolean).join(' + ');
      if (alerted) {
        toast.warning(`${passed}/${total} (${pass_rate_pct}%) — alert sent via ${channels}`);
      } else if (pass_rate_pct < threshold) {
        toast.error(`${passed}/${total} (${pass_rate_pct}%) — regression but all alert channels FAILED`);
      } else {
        toast.success(`${passed}/${total} (${pass_rate_pct}%) — above ${threshold}% threshold, no alert`);
      }
      await loadHistory();
      await loadWatchdog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Watchdog run failed');
    } finally { setRunningWatchdog(false); }
  };

  const testSlack = async () => {
    const url = (watchdog?.slack_webhook_url || '').trim();
    if (!url) { toast.error('Paste a Slack webhook URL first'); return; }
    setTestingSlack(true);
    try {
      await axios.post(`${API}/email-config/watchdog/test-slack`, { slack_webhook_url: url }, { headers });
      toast.success('Test message delivered to Slack — check the channel');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Slack test failed');
    } finally { setTestingSlack(false); }
  };

  const loadDigest = async () => {
    try {
      const res = await axios.get(`${API}/admin/automation-digest/config`, { headers });
      setDigest(res.data);
    } catch (_) { /* non-fatal */ }
  };

  const saveDigest = async () => {
    if (!digest) return;
    setSavingDigest(true);
    try {
      const payload = {
        enabled: !!digest.enabled,
        day_of_week: digest.day_of_week || 'mon',
        hour: Number(digest.hour ?? 8),
        recipient_email: (digest.recipient_email || '').trim(),
        slack_webhook_url: (digest.slack_webhook_url || '').trim(),
      };
      const res = await axios.put(`${API}/admin/automation-digest/config`, payload, { headers });
      setDigest(res.data);
      toast.success(payload.enabled ? 'Digest armed — will send every week' : 'Digest disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save digest');
    } finally { setSavingDigest(false); }
  };

  const runDigestNow = async () => {
    setRunningDigest(true);
    try {
      const res = await axios.post(`${API}/admin/automation-digest/run-now`, {}, { headers });
      const { regressions, total_runs, alerted_email, alerted_slack, email_error, slack_error } = res.data;
      const channels = [alerted_email && 'email', alerted_slack && 'Slack'].filter(Boolean).join(' + ') || 'none';
      const regCount = (regressions || []).length;
      if (alerted_email || alerted_slack) {
        toast.success(`Digest sent via ${channels} — ${total_runs} runs, ${regCount} regressions`);
      } else {
        toast.error(`Dispatch failed — ${email_error || slack_error || 'no channels configured'}`);
      }
      await loadDigest();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digest run failed');
    } finally { setRunningDigest(false); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/email-config/readiness-history`, { headers });
      setHistory(res.data.runs || []);
    } catch (e) {
      // non-fatal — keep page usable
    } finally { setHistoryLoading(false); }
  };

  const exportHistoryCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await axios.get(`${API}/email-config/readiness-history.csv?days=90`, {
        headers, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `kstreak-readiness-history-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Readiness history exported (last 90 days)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExportingCsv(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, domRes] = await Promise.all([
        axios.get(`${API}/email-config`, { headers }),
        axios.get(`${API}/email-config/domains`, { headers }).catch(() => ({ data: { domains: [] } })),
      ]);
      setConfig(cfgRes.data);
      setEditedSender(cfgRes.data.sender_email);
      setDomains(domRes.data.domains || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email config');
    } finally { setLoading(false); }
  };

  const saveSender = async () => {
    setSavingSender(true);
    try {
      await axios.put(`${API}/email-config`, { sender_email: editedSender }, { headers });
      toast.success('Sender email updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSavingSender(false); }
  };

  const addDomain = async () => {
    if (!newDomain || !newDomain.includes('.')) { toast.error('Enter a valid domain'); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/email-config/domains`, { name: newDomain.trim() }, { headers });
      toast.success(`Domain "${newDomain}" added — configure DNS records below`);
      setNewDomain('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally { setAdding(false); }
  };

  const fetchRecords = async (id) => {
    try {
      const res = await axios.get(`${API}/email-config/domains/${id}`, { headers });
      setDomainRecords(prev => ({ ...prev, [id]: res.data }));
    } catch (e) {
      toast.error('Failed to load DNS records');
    }
  };

  const verifyDomain = async (id, name) => {
    try {
      await axios.post(`${API}/email-config/domains/${id}/verify`, {}, { headers });
      toast.success(`Verification triggered for ${name} — DNS checks may take a few minutes`);
      await fetchRecords(id);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verify failed'); }
  };

  const deleteDomain = async (id, name) => {
    if (!window.confirm(`Remove ${name} from Resend?`)) return;
    try {
      await axios.delete(`${API}/email-config/domains/${id}`, { headers });
      toast.success(`${name} removed`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleExpanded = (id) => {
    if (expandedDomain === id) { setExpandedDomain(null); return; }
    setExpandedDomain(id);
    if (!domainRecords[id]) fetchRecords(id);
  };

  const runReadinessCheck = async () => {
    setChecking(true);
    setCheckResults(null);
    try {
      const res = await axios.post(`${API}/email-config/readiness-check`, {}, { headers });
      setCheckResults(res.data);
      loadHistory();
      if (res.data.all_ok) {
        toast.success(`All ${res.data.total} email pathways passed — production ready`);
      } else {
        toast.warning(`${res.data.passed}/${res.data.total} pathways passed — inspect results below`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Readiness check failed');
    } finally { setChecking(false); }
  };

  const runDmarcCheck = async () => {
    setDmarcChecking(true);
    setDmarcResult(null);
    try {
      const q = dmarcDomain.trim() ? `?domain=${encodeURIComponent(dmarcDomain.trim())}` : '';
      const res = await axios.get(`${API}/email-config/dmarc-check${q}`, { headers });
      setDmarcResult(res.data);
      if (res.data.severity === 'ok') {
        toast.success(`DMARC for ${res.data.domain}: ${res.data.summary}`);
      } else if (res.data.severity === 'medium') {
        toast.warning(`DMARC for ${res.data.domain}: ${res.data.summary}`);
      } else {
        toast.error(`DMARC for ${res.data.domain}: ${res.data.summary}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'DMARC check failed');
    } finally { setDmarcChecking(false); }
  };

  const fetchDmarcSummary = async (days) => {
    try {
      const res = await axios.get(`${API}/email-config/dmarc-reports/summary?days=${days || dmarcDays}`, { headers });
      setDmarcSummary(res.data);
    } catch (e) {
      // silent: empty state handled in UI
    }
  };

  const uploadDmarcReport = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    setDmarcUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/email-config/dmarc-reports/ingest`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.ingested > 0) {
        toast.success(`Ingested ${res.data.ingested} DMARC report${res.data.ingested === 1 ? '' : 's'}${res.data.duplicates_skipped ? ` (${res.data.duplicates_skipped} duplicate${res.data.duplicates_skipped === 1 ? '' : 's'} skipped)` : ''}`);
      } else if (res.data.duplicates_skipped > 0) {
        toast.info('Report already ingested (duplicate)');
      } else {
        toast.warning('No reports extracted — file may be malformed');
      }
      await fetchDmarcSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setDmarcUploading(false);
      evt.target.value = '';  // reset so same file can be re-uploaded
    }
  };

  useEffect(() => {
    if (token) {
      fetchDmarcSummary(dmarcDays);
      fetchBlocklistStats();
    }
  }, [token, dmarcDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBlocklistStats = async () => {
    setBlocklistLoading(true);
    try {
      const res = await axios.get(`${API}/email-config/dmarc-blocklist/stats`, { headers });
      setBlocklistStats(res.data);
    } catch (e) {
      // silent: empty state handled in UI
    } finally {
      setBlocklistLoading(false);
    }
  };

  const copyBlocklistUrl = () => {
    const full = `${window.location.origin}${blocklistStats?.feed_url_path || '/api/email-config/dmarc-blocklist.txt'}`;
    navigator.clipboard.writeText(full);
    toast.success('Blocklist URL copied to clipboard');
  };


  const PATHWAY_LABELS = {
    compliance_digest: 'Compliance Digest (weekly)',
    certification_expiry: 'Certification Expiry Reminders (daily)',
    assessment_reminder: 'Assessment Reminders (daily)',
    renewal_quarterly_digest: 'Renewal Readiness Digest (quarterly · PDF attachment)',
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;
  if (!config) return null;

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid="email-config-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2">
          <Mail className="w-6 h-6 text-[#002FA7]" />Email Delivery Configuration
        </h1>
        <p className="text-sm text-[#4B5563] mt-1">
          Manage your Resend integration and verify sender domains. Once a domain is verified, KStreak can send production emails from any address on that domain.
        </p>
      </div>

      {/* Status Card */}
      <Card className="border-[#E5E7EB]" data-testid="status-card">
        <CardContent className="p-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Resend API Key</p>
              <div className="flex items-center gap-2 mt-1">
                {config.resend_configured ? (
                  <><CheckCircle2 className="w-4 h-4 text-[#34C759]" /><span className="text-sm font-medium text-[#0A0A0A]">Configured</span></>
                ) : (
                  <><XCircle className="w-4 h-4 text-[#FF3B30]" /><span className="text-sm font-medium text-[#FF3B30]">Missing</span></>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Current Sender</p>
              <p className="text-sm font-medium text-[#0A0A0A] mt-1 break-all">{config.sender_email}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Last Updated</p>
              <p className="text-sm text-[#4B5563] mt-1">
                {config.updated_at ? `${format(new Date(config.updated_at), 'MMM d, HH:mm')} by ${config.updated_by_name || '—'}` : 'Using default'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Real-time Delivery Webhook */}
      {webhookCfg && (
        <Card className="border-[#E5E7EB]" data-testid="webhook-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#002FA7]" />Real-time Delivery Webhook
                {webhookCfg.configured ? (
                  <Badge className="text-[10px] bg-[#34C759]/15 text-[#34C759] border-0" data-testid="webhook-configured-badge">Live</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-[#FF9500] border-[#FF9500]/40" data-testid="webhook-unconfigured-badge">Secret missing</Badge>
                )}
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={loadWebhookCfg} data-testid="webhook-refresh-btn">
                <RefreshCcw className="w-3 h-3 mr-1" />Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-[11px] text-[#4B5563] mb-3">
              Paste this URL into <a href={webhookCfg.setup_url} target="_blank" rel="noreferrer" className="underline">resend.com/webhooks</a>, enable the 8 event types below, and copy the <strong>signing secret</strong> Resend returns into your server's <code className="text-[10px] bg-[#F3F4F6] px-1 rounded">RESEND_WEBHOOK_SECRET</code> env var. Once set, delivery/bounce/open/click events stream into <a href="/inbox" className="underline">Inbox → Sent Emails</a> in real-time (no polling).
            </p>
            <Label className="text-[10px] text-[#4B5563]">Webhook URL</Label>
            <div className="flex gap-2 mt-1 mb-3">
              <Input
                readOnly value={webhookCfg.webhook_url}
                className="flex-1 font-mono text-xs bg-[#FAFAFA]"
                data-testid="webhook-url-input"
              />
              <Button size="sm" variant="outline" onClick={copyWebhookUrl} data-testid="webhook-copy-btn">
                <Copy className="w-3 h-3 mr-1" />Copy
              </Button>
            </div>
            <Label className="text-[10px] text-[#4B5563]">Event types to enable ({webhookCfg.event_types_to_enable?.length || 0})</Label>
            <div className="flex flex-wrap gap-1 mt-1" data-testid="webhook-event-list">
              {(webhookCfg.event_types_to_enable || []).map(ev => (
                <code key={ev} className="text-[10px] bg-[#F3F4F6] px-1.5 py-0.5 rounded">{ev}</code>
              ))}
            </div>
            {!webhookCfg.configured && (
              <p className="text-[10px] text-[#FF9500] mt-3" data-testid="webhook-secret-missing-hint">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Set <code>RESEND_WEBHOOK_SECRET</code> in <code>/app/backend/.env</code> (starts with <code>whsec_...</code>) and restart the backend.
              </p>
            )}
          </CardContent>
        </Card>
      )}



      {/* Sender Email editor */}
      <Card className="border-[#E5E7EB]" data-testid="sender-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Sender Email Address</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-[10px] text-[#4B5563]">From address (must use a verified domain once sandbox is exited)</Label>
              <Input
                value={editedSender}
                onChange={e => setEditedSender(e.target.value)}
                placeholder="alerts@yourdomain.com"
                className="mt-1"
                data-testid="sender-email-input"
              />
            </div>
            <Button onClick={saveSender} disabled={savingSender || editedSender === config.sender_email} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-sender-btn">
              {savingSender ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Save
            </Button>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-2">
            <AlertTriangle className="w-3 h-3 inline mr-1 text-[#FF9500]" />
            Using <strong>onboarding@resend.dev</strong> restricts delivery to your account-owner email. Verify your own domain below to send to any recipient.
          </p>
        </CardContent>
      </Card>

      {/* Readiness Check */}
      <Card className="border-[#E5E7EB]" data-testid="readiness-check-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#002FA7]" />Production Email Readiness Check
            </CardTitle>
            <Button
              size="sm" onClick={runReadinessCheck} disabled={checking}
              className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="run-readiness-check-btn"
            >
              {checking ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Activity className="w-3 h-3 mr-1" />}
              Run Check
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-[11px] text-[#4B5563] mb-3">
            Sends one <code className="text-[10px] bg-[#F3F4F6] px-1 rounded">[TEST]</code> email to <strong>{config.updated_by_name ? '' : ''}your own address</strong> for each of the 4 production email pathways. Use this after verifying a new domain or changing the sender address to confirm everything still works — replaces ~10 minutes of manual testing.
          </p>
          {checkResults ? (
            <div data-testid="readiness-results">
              <div className={`p-3 rounded-lg mb-2 ${checkResults.all_ok ? 'bg-[#34C759]/10' : 'bg-[#FF9500]/10'}`}>
                <p className="text-sm font-semibold flex items-center gap-2" style={{ color: checkResults.all_ok ? '#34C759' : '#FF9500' }}>
                  {checkResults.all_ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  {checkResults.passed}/{checkResults.total} pathways passed
                </p>
                <p className="text-[10px] text-[#4B5563] mt-1">
                  Sent from <code>{checkResults.from_address}</code> to <code>{checkResults.recipient}</code>
                </p>
              </div>
              <div className="space-y-1.5">
                {checkResults.results.map(r => (
                  <div
                    key={r.pathway}
                    className="flex items-start gap-2 p-2 rounded border border-[#E5E7EB]"
                    data-testid={`readiness-result-${r.pathway}`}
                  >
                    {r.passed ? <CheckCircle2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-[#FF3B30] mt-0.5 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#0A0A0A]">{PATHWAY_LABELS[r.pathway] || r.pathway}</p>
                      {r.error && (
                        <p className="text-[10px] text-[#FF3B30] mt-0.5 break-words">
                          {r.error}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-[#9CA3AF] italic">Click <strong>Run Check</strong> to dispatch 4 test messages.</p>
          )}
        </CardContent>
      </Card>

      {/* DMARC Record Checker */}
      <DmarcCheckCard
        dmarcDomain={dmarcDomain}
        setDmarcDomain={setDmarcDomain}
        dmarcChecking={dmarcChecking}
        dmarcResult={dmarcResult}
        runDmarcCheck={runDmarcCheck}
      />

      {/* DMARC Aggregate Reports Dashboard */}
      <DmarcReportsCard
        dmarcSummary={dmarcSummary}
        dmarcDays={dmarcDays}
        setDmarcDays={setDmarcDays}
        dmarcUploading={dmarcUploading}
        uploadDmarcReport={uploadDmarcReport}
      />

      {/* DMARC Blocklist Feed — plain-text CIDR feed for MTAs/WAF consumers */}
      <DmarcBlocklistCard
        blocklistStats={blocklistStats}
        blocklistLoading={blocklistLoading}
        fetchBlocklistStats={fetchBlocklistStats}
        copyBlocklistUrl={copyBlocklistUrl}
        apiOrigin={API.replace('/api', '')}
      />

      {/* Per-tier A/B metrics for the mood-adaptive weekly-digest subject line */}
      <DigestSubjectMetricsCard />

      {/* PagerDuty incident paging status + 24h audit log */}
      <PagerDutyCard />

      {/* Readiness History */}
      <ReadinessHistoryCard
        history={history}
        historyLoading={historyLoading}
        exportingCsv={exportingCsv}
        exportHistoryCsv={exportHistoryCsv}
        loadHistory={loadHistory}
      />

      {/* Readiness Watchdog */}
      <WatchdogCard
        watchdog={watchdog}
        setWatchdog={setWatchdog}
        runningWatchdog={runningWatchdog}
        runWatchdogNow={runWatchdogNow}
        testingSlack={testingSlack}
        testSlack={testSlack}
        savingWatchdog={savingWatchdog}
        saveWatchdog={saveWatchdog}
      />

      {/* Automation Health Digest */}
      <DigestCard
        digest={digest}
        setDigest={setDigest}
        runningDigest={runningDigest}
        runDigestNow={runDigestNow}
        savingDigest={savingDigest}
        saveDigest={saveDigest}
      />


      {/* Domains */}
      <DomainsCard
        domains={domains}
        newDomain={newDomain}
        setNewDomain={setNewDomain}
        adding={adding}
        expandedDomain={expandedDomain}
        domainRecords={domainRecords}
        onRefresh={load}
        onAddDomain={addDomain}
        onToggleExpanded={toggleExpanded}
        onVerify={verifyDomain}
        onDelete={deleteDomain}
      />
    </div>
  );
}
